import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { CopilotAppComposition } from "@/app/copilot-app-composition";
import { PresenceBoundary } from "@/app/presence-boundary";
import { ROUTES } from "@/app/routes";
import { approvalsRouteSearchSchema } from "@/features/approvals/routes/approvals.route.search";
import {
  attendanceDayRouteSearchSchema,
  attendanceRouteSearchSchema,
} from "@/features/attendance/routes/attendance.route.search";
import { attendanceSettingsRouteSearchSchema } from "@/features/attendance/routes/attendance-settings.route.search";
import { pricingSearchSchema } from "@/features/billing/pricing/pricing.route.search";
import { comparisonRouteSearchSchema } from "@/features/comparison/routes/comparison.route.search";
import { compCycleSearchSchema } from "@/features/compensation/routes/comp-cycle.route.search";
import { compensationRouteSearchSchema } from "@/features/compensation/routes/compensation.route.search";
import { copilotStaticData } from "@/features/copilot/lib/copilot-route-catalog";
import { advancesRouteSearchSchema } from "@/features/expenses/routes/advances.route.search";
import { expensesRouteSearchSchema } from "@/features/expenses/routes/expenses.route.search";
import { travelRouteSearchSchema } from "@/features/expenses/routes/travel.route.search";
import { adminFeedbackRouteSearchSchema } from "@/features/feedback/routes/admin-feedback.route.search";
import { feedbackRouteSearchSchema } from "@/features/feedback/routes/feedback.route.search";
import { feedbackDetailSearchSchema } from "@/features/feedback/routes/feedback-detail.route.search";
import { goalsSearchSchema } from "@/features/goals/routes/goals.route.search";
import { homeRouteSearchSchema } from "@/features/home/routes/home.route.search";
import { hrSettingsSearchSchema } from "@/features/hr-settings/routes/hr-settings.route.search";
import { AcceptInviteRoute } from "@/features/identity/routes/accept-invite.route";
import { AccountSettingsRoute } from "@/features/identity/routes/account-settings.route";
import {
  authErrorSearchSchema,
  authTokenSearchSchema,
  googleHandoffSearchSchema,
  loginSearchSchema,
  signupSearchSchema,
  verifySearchSchema,
} from "@/features/identity/routes/auth.route.search";
import { AuthErrorRoute } from "@/features/identity/routes/auth-error.route";
import { ForgotPasswordRoute } from "@/features/identity/routes/forgot-password.route";
import { GoogleSignInPickAccountRoute } from "@/features/identity/routes/google-pick-account.route";
import { LoginRoute } from "@/features/identity/routes/login.route";
import { profileSearchSchema } from "@/features/identity/routes/profile.route.search";
import { ResetPasswordRoute } from "@/features/identity/routes/reset-password.route";
import { SignupRoute } from "@/features/identity/routes/signup.route";
import { VerifyRoute } from "@/features/identity/routes/verify.route";
import { WorkspaceRedirectRoute } from "@/features/identity/routes/workspace-redirect.route";
import { WorkspaceSettingsRoute } from "@/features/identity/routes/workspace-settings.route";
import { insightsSearchSchema } from "@/features/insights/routes/insights.route.search";
import { kudosRouteSearchSchema } from "@/features/kudos/routes/kudos.route.search";
import { leaveRouteSearchSchema } from "@/features/leave/routes/leave.route.search";
import { leaveBalancesRouteSearchSchema } from "@/features/leave/routes/leave-balances.route.search";
import { leaveHistoryRouteSearchSchema } from "@/features/leave/routes/leave-history.route.search";
import { leaveSettingsRouteSearchSchema } from "@/features/leave/routes/leave-settings.route.search";
import { leaveTeamRouteSearchSchema } from "@/features/leave/routes/leave-team.route.search";
import { notificationsRouteSearchSchema } from "@/features/notifications/routes/notifications.route.search";
import { oneOnOneRouteSearchSchema } from "@/features/one-on-one/routes/one-on-one.route.search";
import { oneOnOneDetailRouteSearchSchema } from "@/features/one-on-one/routes/one-on-one-detail.route.search";
import { oneOnOneWithRouteSearchSchema } from "@/features/one-on-one/routes/one-on-one-with.route.search";
import { orgChartSearchSchema } from "@/features/org-chart/routes/org-chart.route.search";
import { benefitsRouteSearchSchema } from "@/features/payroll/routes/benefits.route.search";
import { moneyAuditRouteSearchSchema } from "@/features/payroll/routes/money-audit.route.search";
import { myPayDetailsRouteSearchSchema } from "@/features/payroll/routes/my-pay-details.route.search";
import { myPayslipsRouteSearchSchema } from "@/features/payroll/routes/my-payslips.route.search";
import { parallelRunRouteSearchSchema } from "@/features/payroll/routes/parallel-run.route.search";
import { payrollInputsRouteSearchSchema } from "@/features/payroll/routes/payroll-inputs.route.search";
import { parsePayrollRunDetailSearch } from "@/features/payroll/routes/payroll-run-detail.route.search";
import { payrollRunsRouteSearchSchema } from "@/features/payroll/routes/payroll-runs.route.search";
import { payrollSalariesRouteSearchSchema } from "@/features/payroll/routes/payroll-salaries.route.search";
import { payrollSetupRouteSearchSchema } from "@/features/payroll/routes/payroll-setup.route.search";
import { recoverySchedulesRouteSearchSchema } from "@/features/payroll/routes/recovery-schedules.route.search";
import { settlementsRouteSearchSchema } from "@/features/payroll/routes/settlements.route.search";
import { taxCalculatorRouteSearchSchema } from "@/features/payroll/routes/tax-calculator.route.search";
import { taxDeclarationsRouteSearchSchema } from "@/features/payroll/routes/tax-declarations.route.search";
import { variablePayPayoutRouteSearchSchema } from "@/features/payroll/routes/variable-pay-payout.route.search";
import { myTeamSearchSchema } from "@/features/people/routes/my-team.route.search";
import { peopleDetailSearchSchema } from "@/features/people/routes/people-detail.route.search";
import { peopleDirectorySearchSchema } from "@/features/people/routes/people-directory.route.search";
import { myActionsSearchSchema } from "@/features/pulse-actions/routes/my-actions.route.search";
import { workspaceActionsSearchSchema } from "@/features/pulse-actions/routes/workspace-actions.route.search";
import { workspaceDashboardSearchSchema } from "@/features/pulse-dashboards/routes/workspace.route.search";
import { myPulseSearchSchema } from "@/features/pulse-surveys/routes/my-pulse.route.search";
import { pulseAnswerThreadsSearchSchema } from "@/features/pulse-surveys/routes/pulse-answer-threads.route.search";
import { pulseSurveyDetailSearchSchema } from "@/features/pulse-surveys/routes/pulse-survey-detail.route.search";
import { pulseSurveysListSearchSchema } from "@/features/pulse-surveys/routes/pulse-surveys-list.route.search";
import { interviewsRouteSearchSchema } from "@/features/recruitment/routes/interviews.route.search";
import { offersRouteSearchSchema } from "@/features/recruitment/routes/offers.route.search";
import {
  recruitmentRequisitionsSearchSchema,
  recruitmentStaffingPlansSearchSchema,
} from "@/features/recruitment/routes/recruitment-governance.route.search";
import { recruitmentHomeSearchSchema } from "@/features/recruitment/routes/recruitment-home.route.search";
import { recruitmentJobSearchSchema } from "@/features/recruitment/routes/recruitment-job.route.search";
import { referralsRouteSearchSchema } from "@/features/recruitment/routes/referrals.route.search";
import { appraisalSchedulesSearchSchema } from "@/features/reviews/routes/appraisal-schedules.route.search";
import { reviewCycleDetailSearchSchema } from "@/features/reviews/routes/review-cycle-detail.route.search";
import { reviewInsightsSearchSchema } from "@/features/reviews/routes/review-insights.route.search";
import { reviewsRouteSearchSchema } from "@/features/reviews/routes/reviews.route.search";
import { adminTodosSearchSchema } from "@/features/todos/routes/admin-todos.route.search";
import { myTodosSearchSchema } from "@/features/todos/routes/my-todos.route.search";
import { useAuth } from "@/shared/auth/auth.context";
import { CommandMenuProvider } from "@/shared/components/command-menu/command-menu.context";
import { CopilotLayoutProvider } from "@/shared/components/layout/copilot-layout.context";
import { ActiveWorkspaceProvider } from "@/shared/hooks/use-active-workspace";
import { INSIGHTS_SEARCH_DEFAULTS } from "@/shared/pulse/insights/insights-search-defaults";

function AppLayoutShell() {
  return (
    <ActiveWorkspaceProvider>
      <PresenceBoundary>
        <CommandMenuProvider>
          <CopilotLayoutProvider>
            <CopilotAppComposition>
              <Outlet />
            </CopilotAppComposition>
          </CopilotLayoutProvider>
        </CommandMenuProvider>
      </PresenceBoundary>
    </ActiveWorkspaceProvider>
  );
}

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

// -- App layout (sidebar + topbar) --------------------------------------------
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app-layout",
  component: AppLayoutShell,
});

// -- Top-level routes (not workspace-scoped) ----------------------------------

const indexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.root.path,
  // Both keys arrive from redirects this app does not control: `firstRun` from
  // signup-complete, and `workspace` (plus `firstRun`) from the shore's Google
  // callback. The schema is strict, so a key missing here rejects the whole
  // URL and the app catch-boundaries into "Something went wrong".
  validateSearch: z
    .object({
      firstRun: z.coerce.number().int().optional(),
      workspace: z.string().optional(),
    })
    .strict(),
  component: WorkspaceRedirectRoute,
});

const designSystemRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.designSystem.path,
  component: lazyRouteComponent(
    () => import("@/features/design-system/routes/design-system.route"),
    "DesignSystemRoute",
  ),
});

// -- Settings --------------------------------------------------------------
// The rail is a COMPONENT (`SettingsLayout`), not a layout route: the two
// workspace sections keep their own `/<slug>/...` paths and still render it,
// so no rail entry ejects you from the surface it belongs to. Section
// components render without their own page container; the layout owns the
// max-width, padding, and the "Settings" heading.

// Bare /settings has no content of its own; Profile is the first section.
const settingsIndexRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.settings.path,
  beforeLoad: () => {
    throw redirect({ to: ROUTES.settingsProfile.path, replace: true });
  },
});

const settingsProfileRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.settingsProfile.path,
  component: lazyRouteComponent(
    () => import("@/features/settings/settings-profile.route"),
    "SettingsProfileRoute",
  ),
});

const settingsTokensRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.settingsTokens.path,
  component: lazyRouteComponent(
    () => import("@/features/api-tokens/routes/api-tokens.route"),
    "ApiTokensRoute",
  ),
});

const settingsMcpRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.settingsMcp.path,
  component: lazyRouteComponent(
    () => import("@/features/ai-connections/routes/mcp.route"),
    "McpRoute",
  ),
});

const settingsAiToolsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.settingsAiTools.path,
  beforeLoad: () => {
    throw redirect({ to: ROUTES.settingsMcp.path, replace: true });
  },
});

const settingsAiConnectionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.settingsAiConnections.path,
  component: lazyRouteComponent(
    () => import("@/features/ai-connections/routes/ai-connections.route"),
    "AiConnectionsRoute",
  ),
});

const settingsAccountRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.settingsAccount.path,
  component: AccountSettingsRoute,
});

const settingsBillingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.settingsBilling.path,
  component: lazyRouteComponent(
    () => import("@/features/billing/account/account-billing.route"),
    "AccountBillingRoute",
  ),
});

// Old account-level paths: kept as redirects rather than deleted, since
// bookmarks, the upgrade dialog, every FeatureGate card, and the redeem
// screen's post-redemption navigate all already point at them.
const accountSettingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.accountSettings.path,
  beforeLoad: () => {
    throw redirect({ to: ROUTES.settingsAccount.path, replace: true });
  },
});

const accountBillingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.accountBilling.path,
  beforeLoad: () => {
    throw redirect({ to: ROUTES.settingsBilling.path, replace: true });
  },
});

const billingRedeemRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.billingRedeem.path,
  component: lazyRouteComponent(
    () => import("@/features/billing/redeem/redeem.route"),
    "RedeemCodeRoute",
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.profile.path,
  validateSearch: profileSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/identity/routes/profile.route"),
    "ProfileRoute",
  ),
});

// -- Workspace-scoped routes (under /$workspaceSlug) --------------------------
// `workspaceSlug` is the source of truth for which workspace the page shows.
// Switching workspaces via the sidebar navigates to the same sub-path under
// the new slug. Consumers resolve the slug to a Workspace object via
// useCurrentWorkspace() (auth.context).

const workspaceLayoutRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: ROUTES.workspace.home.path,
  component: () => <Outlet />,
});

const workspaceHomeRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/",
  validateSearch: homeRouteSearchSchema,
  component: lazyRouteComponent(() => import("@/features/home/routes/home.route"), "HomeRoute"),
});

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/settings",
  component: WorkspaceSettingsRoute,
});

const workspaceCompanySettingsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/settings/company",
  component: lazyRouteComponent(
    () => import("@/features/identity/routes/company-settings.route"),
    "CompanySettingsRoute",
  ),
});

const peopleDirectoryRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/people",
  staticData: copilotStaticData("/people"),
  validateSearch: peopleDirectorySearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/people/routes/people-directory.route"),
    "PeopleDirectoryRoute",
  ),
});

const peopleDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/people/$membershipId",
  staticData: copilotStaticData("/people/$membershipId"),
  validateSearch: peopleDetailSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/people/routes/people-detail.route"),
    "PeopleDetailRoute",
  ),
});

const peopleEditRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/people/$membershipId/edit",
  component: lazyRouteComponent(
    () => import("@/features/people/routes/people-edit.route"),
    "PeopleEditRoute",
  ),
});

const peopleDetailHrRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/people/$membershipId/hr",
  component: lazyRouteComponent(
    () => import("@/features/people/routes/people-detail-hr.route"),
    "PeopleDetailHrRoute",
  ),
});

const myTeamRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/my-team",
  staticData: copilotStaticData("/my-team"),
  validateSearch: myTeamSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/people/routes/my-team.route"),
    "MyTeamRoute",
  ),
});

const orgChartRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/org-chart",
  staticData: copilotStaticData("/org-chart"),
  validateSearch: orgChartSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/org-chart/routes/org-chart.route"),
    "OrgChartRoute",
  ),
});

const reviewsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews",
  staticData: copilotStaticData("/performance-reviews"),
  validateSearch: reviewsRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/reviews.route"),
    "ReviewsRoute",
  ),
});

const reviewsCyclesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/cycles",
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/reviews-cycles-redirect.route"),
    "ReviewsCyclesRedirectRoute",
  ),
});

const reviewsTemplatesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/templates",
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/question-sets.route"),
    "QuestionSetsRoute",
  ),
});

const reviewsAppraisalSchedulesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/appraisal-schedules",
  validateSearch: appraisalSchedulesSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/appraisal-schedules.route"),
    "AppraisalSchedulesRoute",
  ),
});

const reviewInsightsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/insights",
  validateSearch: reviewInsightsSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-insights.route"),
    "ReviewInsightsRoute",
  ),
});

const reviewCycleDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/cycles/$cycleId",
  staticData: copilotStaticData("/performance-reviews/cycles/$cycleId"),
  validateSearch: reviewCycleDetailSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-cycle-detail.route"),
    "ReviewCycleDetailRoute",
  ),
});

const reviewManagerRatingsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/cycles/$cycleId/manager-ratings",
  staticData: copilotStaticData("/performance-reviews/cycles/$cycleId/manager-ratings"),
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-manager-ratings.route"),
    "ReviewManagerRatingsRoute",
  ),
});

const reviewSelfReviewRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/cycles/$cycleId/self-review",
  staticData: copilotStaticData("/performance-reviews/cycles/$cycleId/self-review"),
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-self-review.route"),
    "ReviewSelfReviewRoute",
  ),
});

const reviewManagerReviewRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/cycles/$cycleId/participants/$participantId/manager-review",
  staticData: copilotStaticData(
    "/performance-reviews/cycles/$cycleId/participants/$participantId/manager-review",
  ),
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-manager.route"),
    "ReviewManagerRoute",
  ),
});

const reviewManagerResultsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/cycles/$cycleId/participants/$participantId/results",
  staticData: copilotStaticData(
    "/performance-reviews/cycles/$cycleId/participants/$participantId/results",
  ),
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-manager-results.route"),
    "ReviewManagerResultsRoute",
  ),
});

const reviewPeerResponseRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/cycles/$cycleId/participants/$participantId/peer-review",
  staticData: copilotStaticData(
    "/performance-reviews/cycles/$cycleId/participants/$participantId/peer-review",
  ),
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-peer.route"),
    "ReviewPeerRoute",
  ),
});

const reviewTemplateViewRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/templates/$templateId/view",
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-template-view.route"),
    "ReviewTemplateViewRoute",
  ),
});

const reviewTemplateEditorRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/performance-reviews/templates/$templateId",
  staticData: copilotStaticData("/performance-reviews/templates/$templateId"),
  component: lazyRouteComponent(
    () => import("@/features/reviews/routes/review-template-editor.route"),
    "ReviewTemplateEditorRoute",
  ),
});

const myTodosRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/todos",
  staticData: copilotStaticData("/todos"),
  validateSearch: myTodosSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/todos/routes/my-todos.route"),
    "MyTodosRoute",
  ),
});

const todoDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/todos/$todoId",
  staticData: copilotStaticData("/todos/$todoId"),
  component: lazyRouteComponent(
    () => import("@/features/todos/routes/todo-detail.route"),
    "TodoDetailRoute",
  ),
});

const adminTodosRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/admin/todos",
  validateSearch: adminTodosSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/todos/routes/admin-todos.route"),
    "AdminTodosRoute",
  ),
});

const goalsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/goals",
  staticData: copilotStaticData("/goals"),
  validateSearch: goalsSearchSchema,
  component: lazyRouteComponent(() => import("@/features/goals/routes/goals.route"), "GoalsRoute"),
});

const goalDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/goals/$goalId",
  staticData: copilotStaticData("/goals/$goalId"),
  component: lazyRouteComponent(
    () => import("@/features/goals/routes/goal-detail.route"),
    "GoalDetailRoute",
  ),
});

const feedbackRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/feedback",
  staticData: copilotStaticData("/feedback"),
  validateSearch: feedbackRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/feedback/routes/feedback.route"),
    "FeedbackRoute",
  ),
});

const feedbackDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/feedback/$feedbackId",
  staticData: copilotStaticData("/feedback/$feedbackId"),
  validateSearch: feedbackDetailSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/feedback/routes/feedback-detail.route"),
    "FeedbackDetailRoute",
  ),
});

const adminFeedbackRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/admin/feedback",
  validateSearch: adminFeedbackRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/feedback/routes/admin-feedback.route"),
    "AdminFeedbackRoute",
  ),
});

const attendanceRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/attendance",
  staticData: copilotStaticData("/attendance"),
  validateSearch: attendanceRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/attendance/routes/attendance.route"),
    "AttendanceRoute",
  ),
});

const attendanceTeamRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/attendance/team",
  staticData: copilotStaticData("/attendance/team"),
  validateSearch: attendanceDayRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/attendance/routes/attendance-team.route"),
    "AttendanceTeamRoute",
  ),
});

// Folded back into the team page as a tab. Deciding a request and reading a
// day are different jobs, but they are the same person's jobs, and a separate
// nav item made a manager's requests a page while an employee's were a tab.
// Kept as a redirect so an existing link still lands.
const attendanceRequestsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/attendance/requests",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: ROUTES.workspace.attendanceTeam.path,
      params,
      search: { tab: "requests" as const },
      replace: true,
    });
  },
});

// The admin board merged into the team page: it called the same endpoint and
// got the same rows, since the server resolves breadth from permissions rather
// than from anything the client sends. Kept as a redirect so existing
// bookmarks and links still land on the page that replaced it.
const attendanceBoardRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/attendance/board",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: ROUTES.workspace.attendanceTeam.path,
      params,
      replace: true,
    });
  },
});

const attendanceSetupRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/settings/attendance",
  validateSearch: attendanceSettingsRouteSearchSchema,
  staticData: copilotStaticData("/settings/attendance"),
  component: lazyRouteComponent(
    () => import("@/features/attendance/routes/attendance-settings.route"),
    "AttendanceSettingsRoute",
  ),
});

// -- Module 17: HRMS Leave ----------------------------------------------------

const leaveRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/leave",
  staticData: copilotStaticData("/leave"),
  validateSearch: leaveRouteSearchSchema,
  component: lazyRouteComponent(() => import("@/features/leave/routes/leave.route"), "LeaveRoute"),
});

const leaveHistoryRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/leave/history",
  staticData: copilotStaticData("/leave/history"),
  validateSearch: leaveHistoryRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/leave/routes/leave-history.route"),
    "LeaveHistoryRoute",
  ),
});

const leaveTeamRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/leave/team",
  staticData: copilotStaticData("/leave/team"),
  validateSearch: leaveTeamRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/leave/routes/leave-team.route"),
    "LeaveTeamRoute",
  ),
});

const leaveBalancesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/leave/balances",
  staticData: copilotStaticData("/leave/balances"),
  validateSearch: leaveBalancesRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/leave/routes/leave-balances.route"),
    "LeaveBalancesRoute",
  ),
});

const leaveSetupRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/settings/leave",
  validateSearch: leaveSettingsRouteSearchSchema,
  staticData: copilotStaticData("/settings/leave"),
  component: lazyRouteComponent(
    () => import("@/features/leave/routes/leave-settings.route"),
    "LeaveSettingsRoute",
  ),
});

// -- HRMS Compensation: scope tabs + cycle hub (CMP-002) ----------------------

const compensationRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/compensation",
  validateSearch: compensationRouteSearchSchema,
  // Lazy (P1.1): the page pulls in the cycles table and the start dialog,
  // and the entitlement is dark on every plan today.
  component: lazyRouteComponent(
    () => import("@/features/compensation/routes/compensation.route"),
    "CompensationRoute",
  ),
});

const compCycleRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/compensation/cycles/$cycleId",
  validateSearch: compCycleSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/compensation/routes/comp-cycle.route"),
    "CompCycleRoute",
  ),
});

// -- HRMS Payroll: setup (PAY-005) --------------------------------------------

const payrollSetupRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/setup",
  validateSearch: payrollSetupRouteSearchSchema,
  // Lazy (P1.1): the setup screen pulls in the data table, the formula editor
  // and the whole component dialog, none of which belong in the initial chunk.
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/payroll-setup.route"),
    "PayrollSetupRoute",
  ),
});

// -- HRMS Payroll: the member's tax calculator (tax intelligence G-03) --------

const benefitsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  // Relative to `workspaceLayoutRoute`, which already owns `/$workspaceSlug`.
  path: "/payroll/benefits",
  validateSearch: benefitsRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/benefits.route"),
    "BenefitsRoute",
  ),
});

const taxDeclarationsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  // Relative to `workspaceLayoutRoute`, which already owns `/$workspaceSlug`.
  // `ROUTES.workspace.taxDeclarations.path` is the full path, for links.
  path: "/payroll/tax-declarations",
  validateSearch: taxDeclarationsRouteSearchSchema,
  // Lazy (P1.1): the declaration form, the comparison table, the proposal list
  // and the confirm sheet are a screen most people open a few times a year,
  // and none of it belongs in the chunk everybody downloads on sign-in.
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/tax-declarations.route"),
    "TaxDeclarationsRoute",
  ),
});

// -- HRMS Payroll: variable pay (payroll depth F-08) --------------------------

const variablePayRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/variable-pay",
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/variable-pay.route"),
    "VariablePayRoute",
  ),
});

const variablePayPlanRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/variable-pay/$planId",
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/variable-pay-plan.route"),
    "VariablePayPlanRoute",
  ),
});

const variablePayPayoutRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/variable-pay/$planId/batches/$payoutId",
  validateSearch: variablePayPayoutRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/variable-pay-payout.route"),
    "VariablePayPayoutRoute",
  ),
});

// -- HRMS Payroll: parallel run (payroll depth K-05) ---------------------------

const parallelRunRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/parallel-run",
  validateSearch: parallelRunRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/parallel-run.route"),
    "ParallelRunRoute",
  ),
});

// -- HRMS Payroll: member money audit (payroll depth L-06) --------------------

const moneyAuditRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/money-audit",
  validateSearch: moneyAuditRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/money-audit.route"),
    "MoneyAuditRoute",
  ),
});

/**
 * The calculator's old home, kept as a redirect.
 *
 * It shipped as a standalone page with no entry point anywhere in the app, so
 * the only way anybody reached it was a typed or bookmarked URL. Those still
 * work, and land on the tab they meant.
 */
const taxCalculatorRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/tax-calculator",
  validateSearch: taxCalculatorRouteSearchSchema,
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: ROUTES.workspace.taxDeclarations.path,
      params,
      search: { ...search, tab: "calculator" },
      replace: true,
    });
  },
});

// -- HRMS Payroll: salaries grid (CTC redesign) --------------------------------

const payrollSalariesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/salaries",
  validateSearch: payrollSalariesRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/payroll-salaries.route"),
    "PayrollSalariesRoute",
  ),
});

// -- HRMS Recruitment: the jobs list (REC-004, rebuilt by REC-UX P4) ----------

const recruitmentRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment",
  validateSearch: recruitmentHomeSearchSchema,
  // Lazy (P1.1): the page pulls in a table, the stat strip and a create
  // dialog, none of which belong in the initial chunk for a section most
  // people never open.
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/recruitment-home.route"),
    "RecruitmentHomeRoute",
  ),
});

// -- HRMS Recruitment: one job (REC-UX P4.5) ----------------------------------

/// The board lives here now, scoped to one opening, alongside that job's
/// interviews, offers and letters. The five-tab strip this replaced is gone.
const recruitmentJobRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment/openings/$openingId",
  validateSearch: recruitmentJobSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/recruitment-job.route"),
    "RecruitmentJobRoute",
  ),
});

// -- HRMS Recruitment: the workspace-wide surfaces (REC-UX P4) ----------------

/// Requisitions, staffing plans and letters were tabs on the page above until
/// P4. They are siblings rather than chips on a job because each is
/// workspace-wide: a requisition exists before any opening does, a staffing
/// plan caps several, and a letter template is written once and used by all.
const recruitmentRequisitionsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment/requisitions",
  validateSearch: recruitmentRequisitionsSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/recruitment-governance.route"),
    "RecruitmentRequisitionsRoute",
  ),
});

const recruitmentStaffingPlansRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment/staffing-plans",
  validateSearch: recruitmentStaffingPlansSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/recruitment-governance.route"),
    "RecruitmentStaffingPlansRoute",
  ),
});

const recruitmentLettersRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment/letters",
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/recruitment-letters.route"),
    "RecruitmentLettersRoute",
  ),
});

// -- HRMS Recruitment: applicant detail (REC-011) -----------------------------

const recruitmentApplicantDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment/applicants/$applicantId",
  // Lazy (P1.1): the detail page pulls in the timeline kit, the attachments
  // HTTP layer and the stage menu. None of it belongs in the initial chunk of
  // a page most people never open.
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/applicant-detail.route"),
    "ApplicantDetailRoute",
  ),
});

// -- HRMS Recruitment: offers and the hire conversion (REC-031) ---------------

const recruitmentOffersRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment/offers",
  validateSearch: offersRouteSearchSchema,
  // Lazy (P1.1): the page pulls in the data table, a rows editor and four
  // dialogs, one of which drags in the seat meter. None of it belongs in the
  // initial chunk of a screen only hiring admins open.
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/offers.route"),
    "OffersRoute",
  ),
});

// -- HRMS Recruitment: referrals (REC-040) ------------------------------------

const recruitmentReferralsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment/referrals",
  validateSearch: referralsRouteSearchSchema,
  // Lazy (P1.1): the page pulls in the data table, the attachment kit and two
  // dialogs, none of which belong in the initial chunk.
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/referrals.route"),
    "ReferralsRoute",
  ),
});

// -- HRMS Recruitment: interviews (REC-021) -----------------------------------

const recruitmentInterviewsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/recruitment/interviews",
  validateSearch: interviewsRouteSearchSchema,
  // Lazy (P1.1): the page pulls in the data table, the date picker and three
  // dialogs, none of which belong in the initial chunk.
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/interviews.route"),
    "InterviewsRoute",
  ),
});

// -- HRMS Payroll: run list + run wizard (PAY-022) ----------------------------

const payrollRunsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/runs",
  validateSearch: payrollRunsRouteSearchSchema,
  // Lazy (P1.1): the list pulls in the data table and the create dialog, and
  // the wizard beside it pulls in the whole run feature. Neither belongs in
  // the initial chunk of a page most people never open.
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/payroll-runs.route"),
    "PayrollRunsRoute",
  ),
});

// -- HRMS Payroll: the payroll inputs grid (PAYD-FR-001 to 008, FR-026) ------

const payrollInputsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/inputs",
  validateSearch: payrollInputsRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/payroll-inputs.route"),
    "PayrollInputsRoute",
  ),
});

// -- HRMS Payroll: recovery schedules and arrears (PAYD-FR-044, FR-045, FR-047, FR-125) --

const recoverySchedulesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/recoveries",
  validateSearch: recoverySchedulesRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/recovery-schedules.route"),
    "RecoverySchedulesRoute",
  ),
});

// -- HRMS Payroll: settlements (PAYD-FR-030 to FR-034, FR-110, FR-113) -------

const settlementsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/settlements",
  validateSearch: settlementsRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/settlements.route"),
    "SettlementsRoute",
  ),
});

const settlementWorksheetRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/settlements/$settlementId",
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/settlement-worksheet.route"),
    "SettlementWorksheetRoute",
  ),
});

// -- HRMS Payroll: compliance documents (PAYD-FR-050 to FR-065) --------------

const complianceDocumentsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/documents",
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/compliance-documents.route"),
    "ComplianceDocumentsRoute",
  ),
});

const complianceDocumentRunRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/documents/$runId",
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/compliance-document-run.route"),
    "ComplianceDocumentRunRoute",
  ),
});

const payrollRunDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/runs/$runId",
  validateSearch: (search: Record<string, unknown>) => parsePayrollRunDetailSearch(search),
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/payroll-run-detail.route"),
    "PayrollRunDetailRoute",
  ),
});

// -- HRMS Payroll: my payslips (PAY-023) --------------------------------------

const myPayslipsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/payslips",
  validateSearch: myPayslipsRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/my-payslips.route"),
    "MyPayslipsRoute",
  ),
});

// -- HRMS Payroll: my pay details (ESS-01) ------------------------------------

const myPayDetailsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/payroll/my-details",
  validateSearch: myPayDetailsRouteSearchSchema,
  // Lazy for the same reason as the setup screen: this page pulls in five
  // form dialogs, the attachment field and the whole money section, none of
  // which belong in the initial chunk.
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/my-pay-details.route"),
    "MyPayDetailsRoute",
  ),
});

// -- HRMS Core: HR settings (FND-017) -----------------------------------------

const hrSettingsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/settings/hr",
  // Empty today (`hrSettingsSearchSchema` is `z.object({}).strict()`).
  // FND-015 once added `calendarId` here; FND-015b task 2 removed it again
  // when the calendar editor moved to `attendance-settings.route.search.ts`.
  // Still wired so a future schema change needs no edit to this file.
  validateSearch: hrSettingsSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/hr-settings/routes/hr-settings.route"),
    "HrSettingsRoute",
  ),
});

// -- HRMS: unified approvals inbox (FND-026) ----------------------------------

const approvalsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/approvals",
  validateSearch: approvalsRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/approvals/routes/approvals.route"),
    "ApprovalsRoute",
  ),
});

// -- HRMS Expenses: claims + category setup (EXP-001) -------------------------

const expensesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/expenses",
  validateSearch: expensesRouteSearchSchema,
  // Lazy (RT1/P1.1): the setup lens pulls in the whole DataTable stack, and
  // the `expenses` entitlement is dark on every plan today, so no one on the
  // current plan registry should be paying for this chunk on first paint.
  component: lazyRouteComponent(
    () => import("@/features/expenses/routes/expenses.route"),
    "ExpensesRoute",
  ),
});

/// Declared BEFORE the claim detail so `/expenses/advances` is matched as
/// itself rather than as a claim whose id happens to be the word "advances".
const expenseAdvancesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/expenses/advances",
  validateSearch: advancesRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/expenses/routes/advances.route"),
    "AdvancesRoute",
  ),
});

const expenseTravelRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/expenses/travel",
  validateSearch: travelRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/expenses/routes/travel.route"),
    "TravelRoute",
  ),
});

const expenseClaimDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/expenses/$claimId",
  // Lazy for the same reason the list is: the detail pulls the attachment and
  // audit kits in behind it, and nobody on the current plan registry can
  // reach either page yet.
  component: lazyRouteComponent(
    () => import("@/features/expenses/routes/claim-detail.route"),
    "ClaimDetailRoute",
  ),
});

const kudosRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/kudos",
  staticData: copilotStaticData("/kudos"),
  validateSearch: kudosRouteSearchSchema,
  component: lazyRouteComponent(() => import("@/features/kudos/routes/kudos.route"), "KudosRoute"),
});

const kudosDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/kudos/$kudosId",
  staticData: copilotStaticData("/kudos/$kudosId"),
  component: lazyRouteComponent(
    () => import("@/features/kudos/routes/kudos-detail.route"),
    "KudosDetailRoute",
  ),
});

const oneOnOneRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/one-on-ones",
  validateSearch: oneOnOneRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/one-on-one/routes/one-on-one.route"),
    "OneOnOneRoute",
  ),
});

const oneOnOneDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/one-on-ones/$oneOnOneId",
  validateSearch: oneOnOneDetailRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/one-on-one/routes/one-on-one-detail.route"),
    "OneOnOneDetailRoute",
  ),
});

const oneOnOneWithRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/one-on-ones/with/$userId",
  validateSearch: oneOnOneWithRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/one-on-one/routes/one-on-one-with.route"),
    "OneOnOneWithRoute",
  ),
});

// -- Module 7: Notifications --------------------------------------------------

const notificationsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/notifications",
  validateSearch: notificationsRouteSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/notifications/routes/notifications.route"),
    "NotificationsRoute",
  ),
});

const notificationsPreferencesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/notifications/preferences",
  component: lazyRouteComponent(
    () => import("@/features/notifications/routes/notifications-preferences.route"),
    "NotificationsPreferencesRoute",
  ),
});

// -- Module 8: Health Inputs + Pulse Surveys ----------------------------------

const pulseSurveysListRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/pulse-surveys",
  staticData: copilotStaticData("/pulse-surveys"),
  validateSearch: pulseSurveysListSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/pulse-surveys-list.route"),
    "PulseSurveysListRoute",
  ),
});

const pulseSurveysNewRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/pulse-surveys/new",
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/pulse-surveys-new.route"),
    "PulseSurveysNewRoute",
  ),
});

const pulseSurveyDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/pulse-surveys/$surveyId",
  staticData: copilotStaticData("/pulse-surveys/$surveyId"),
  validateSearch: pulseSurveyDetailSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/pulse-survey-detail.route"),
    "PulseSurveyDetailRoute",
  ),
});

const pulseSurveyEditRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/pulse-surveys/$surveyId/edit",
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/pulse-survey-edit.route"),
    "PulseSurveyEditRoute",
  ),
});

const pulseSurveyInsightsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  // reason: ROUTES.workspace.pulseSurveyInsights.path is the full /$workspaceSlug/...
  // path but child routes use segments; use the segment so the router tree nests correctly.
  path: "/pulse-surveys/$surveyId/insights",
  // Redirect alias onto the detail page's Results view; shares the detail
  // search schema so run/department survive the forward.
  validateSearch: pulseSurveyDetailSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/pulse-survey-insights.route"),
    "PulseSurveyInsightsRoute",
  ),
});

const workspaceHealthConfigRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/settings/health-config",
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/workspace-health-config.route"),
    "WorkspaceHealthConfigRoute",
  ),
});

const pulseSurveyRespondRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/pulse-surveys/me/deliveries/$deliveryId",
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/pulse-survey-respond.route"),
    "PulseSurveyRespondRoute",
  ),
});

/// Member landing: every survey awaiting their answer, as a card grid. Past
/// responses live one level down at /my-pulse/answered.
const myPulseRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/my-pulse",
  staticData: copilotStaticData("/my-pulse"),
  validateSearch: myPulseSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/my-pulse.route"),
    "MyPulseRoute",
  ),
});

const myPulseAnsweredRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/my-pulse/answered",
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/my-pulse-answered.route"),
    "MyPulseAnsweredRoute",
  ),
});

const myPulseConversationRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/my-pulse/conversations/$threadId",
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/my-pulse-conversation.route"),
    "MyPulseConversationRoute",
  ),
});

const pulseAnswerThreadsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/pulse-surveys/answer-threads",
  validateSearch: pulseAnswerThreadsSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/pulse-answer-threads.route"),
    "PulseAnswerThreadsRoute",
  ),
});

const pulseAnswerThreadRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/pulse-surveys/answer-threads/$threadId",
  component: lazyRouteComponent(
    () => import("@/features/pulse-surveys/routes/pulse-answer-thread.route"),
    "PulseAnswerThreadRoute",
  ),
});

// -- Module 11: Pulse Action Engine ------------------------------------------

const workspaceActionsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/actions",
  staticData: copilotStaticData("/actions"),
  validateSearch: workspaceActionsSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/pulse-actions/routes/workspace-actions.route"),
    "WorkspaceActionsRoute",
  ),
});

const myActionsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/actions/my",
  validateSearch: myActionsSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/pulse-actions/routes/my-actions.route"),
    "MyActionsRoute",
  ),
});

const actionDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/actions/$actionId",
  staticData: copilotStaticData("/actions/$actionId"),
  component: lazyRouteComponent(
    () => import("@/features/pulse-actions/routes/action-detail.route"),
    "ActionDetailRoute",
  ),
});

const actionUpdatesRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/actions/updates",
  component: lazyRouteComponent(
    () => import("@/features/pulse-actions/routes/action-updates.route"),
    "ActionUpdatesRoute",
  ),
});

// -- Module 12: Pulse Audience Dashboards (ADR-0004, single-audience) -------
// Task 16 consolidates every surface below into /insights (see insights.route.tsx).
// /workspace, /workspace/departments/$deptId, /workspace/themes/$theme,
// /workspace/feed, and /comparison are now redirect-only entries (their
// component files are deleted); the legacy /dashboard/* redirects below are
// re-pointed straight at the final /insights (or /actions/updates) targets
// so there is never a redirect chain.

// Legacy /dashboard root, redirect straight to /insights (was /workspace,
// which is itself now a redirect, see workspaceRoute below: never chain).
const legacyDashboardRootRedirect = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/dashboard",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: ROUTES.workspace.insights.path,
      params,
      search: { ...INSIGHTS_SEARCH_DEFAULTS, tab: "overview" },
    });
  },
});

// Old /workspace tab values map 1:1 onto insights tabs, except "insights"
// (the old Insight-queue tab), which is /insights's "queue".
const WORKSPACE_TAB_TO_INSIGHTS_TAB = {
  overview: "overview",
  brief: "brief",
  insights: "queue",
} as const;

const workspaceRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/workspace",
  validateSearch: workspaceDashboardSearchSchema,
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: ROUTES.workspace.insights.path,
      params,
      search: {
        ...INSIGHTS_SEARCH_DEFAULTS,
        tab: search.tab ? WORKSPACE_TAB_TO_INSIGHTS_TAB[search.tab] : "overview",
        window: search.range ?? "30d",
        briefAudience: search.briefAudience,
        insightAudience: search.insightAudience,
        insightScope: search.insightScope,
        insightStatus: search.insightStatus,
        insightSeverity: search.insightSeverity,
        insightId: search.insightId,
      },
    });
  },
});

// ADR-0004, legacy /dashboard/* drill-in paths redirect straight to the
// /insights equivalents (Task 16 re-point; was /workspace/*, itself now a
// redirect, so this skips the chain). The `audience` search param (if any)
// is dropped; the new destination doesn't accept it.
const legacyDashboardDeptRedirect = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/dashboard/departments/$deptId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: ROUTES.workspace.insights.path,
      params,
      search: { ...INSIGHTS_SEARCH_DEFAULTS, tab: "departments", dept: params.deptId },
    });
  },
});

const legacyDashboardThemeRedirect = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/dashboard/themes/$theme",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: ROUTES.workspace.insights.path,
      params,
      search: { ...INSIGHTS_SEARCH_DEFAULTS, tab: "themes", theme: params.theme },
    });
  },
});

// The "you said, we did" feed's content lives on the surviving
// /actions/updates route, not a summary-only insights tab (Task 16, Actions
// tab decision), so this is the honest redirect target, not /insights.
const legacyDashboardFeedRedirect = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/dashboard/feed",
  beforeLoad: ({ params }) => {
    throw redirect({ to: ROUTES.workspace.actionUpdates.path, params, search: {} });
  },
});

// Task 12/16: department drill-in fully replicated by insights' Departments
// tab drill-down drawer (`DeptDetailSheet`).
const dashboardDepartmentDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/workspace/departments/$deptId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: ROUTES.workspace.insights.path,
      params,
      search: { ...INSIGHTS_SEARCH_DEFAULTS, tab: "departments", dept: params.deptId },
    });
  },
});

// Task 15/16: theme drill-in fully replicated by insights' Themes tab
// drill-down drawer (`ThemeDetailSheet`).
const dashboardThemeDetailRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/workspace/themes/$theme",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: ROUTES.workspace.insights.path,
      params,
      search: { ...INSIGHTS_SEARCH_DEFAULTS, tab: "themes", theme: params.theme },
    });
  },
});

// Task 16: same content family as /actions/updates (both wrap
// `useActionUpdatePages`), which stays mounted; that's the honest target,
// not the Actions tab's summary-only view.
const dashboardFeedRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/workspace/feed",
  beforeLoad: ({ params }) => {
    throw redirect({ to: ROUTES.workspace.actionUpdates.path, params, search: {} });
  },
});

// Task 16: fully replicated by insights' Departments tab (the same
// `ComparisonHeatmap` + `useComparison`, filtered via the shared FilterBar).
const comparisonRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/comparison",
  validateSearch: comparisonRouteSearchSchema,
  beforeLoad: ({ params, search }) => {
    throw redirect({
      to: ROUTES.workspace.insights.path,
      params,
      search: {
        ...INSIGHTS_SEARCH_DEFAULTS,
        tab: "departments",
        window: search.range ?? "30d",
        metric: search.metric,
        depts: search.depts.join(","),
      },
    });
  },
});

// -- Insights (Phase 3, consolidates the 8 analytics surfaces into one
// tabbed shell behind a single URL-backed FilterBar). Task 16 finishes the
// consolidation: every route above now redirects here.
const insightsRoute = createRoute({
  getParentRoute: () => workspaceLayoutRoute,
  path: "/insights",
  staticData: copilotStaticData("/insights"),
  validateSearch: insightsSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/insights/routes/insights.route"),
    "InsightsRoute",
  ),
});

// -- Auth layout (no chrome) --------------------------------------------------

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "auth-layout",
  component: () => (
    <div className="min-h-screen bg-background text-foreground">
      <Outlet />
    </div>
  ),
});

const signupRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.auth.signup.path,
  validateSearch: signupSearchSchema,
  component: SignupRoute,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.auth.login.path,
  // `stage` survives tab switches + refresh because the URL is the truth.
  // Email is intentionally NOT in the URL (FE-SEC7, no PII in URLs); the
  // route component persists it in sessionStorage for refresh-survival.
  validateSearch: loginSearchSchema,
  component: LoginRoute,
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.auth.forgotPassword.path,
  validateSearch: z.object({}),
  component: ForgotPasswordRoute,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.auth.resetPassword.path,
  // `token` comes from the emailed reset link; the component handles a missing
  // token with a friendly "link invalid" panel.
  validateSearch: authTokenSearchSchema,
  component: ResetPasswordRoute,
});

const verifyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.auth.verify.path,
  validateSearch: verifySearchSchema,
  component: VerifyRoute,
});

const authErrorRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.auth.authError.path,
  validateSearch: authErrorSearchSchema,
  component: AuthErrorRoute,
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.auth.acceptInvite.path,
  validateSearch: authTokenSearchSchema,
  component: AcceptInviteRoute,
});

const googlePickAccountRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.auth.googlePickAccount.path,
  validateSearch: googleHandoffSearchSchema,
  component: GoogleSignInPickAccountRoute,
});

const pricingRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.pricing.path,
  validateSearch: pricingSearchSchema,
  component: lazyRouteComponent(
    () => import("@/features/billing/pricing/pricing.route"),
    "PricingRoute",
  ),
});

/**
 * The one anonymous page (HRMS-REC-012). Under auth-layout, not app-layout,
 * because it has no workspace to mount a shell around: the token in the path
 * is what resolves the tenant, server side, and nothing on this page reads a
 * session.
 *
 * Lazy despite P1's "auth-layout routes can stay eager" note. That note is
 * about the identity screens, which sit on the initial-paint path for everyone
 * signing in; this page is reached by a handful of strangers following a link,
 * and it drags in react-hook-form, the zod resolver and its own schema. Paying
 * for that in every signed-in user's first chunk buys nothing (P1, budget is
 * 147 of 150 KB gzipped).
 */
const publicApplyRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.apply.path,
  component: lazyRouteComponent(
    () => import("@/features/recruitment/public/apply-page"),
    "ApplyPage",
  ),
});

/// Hosted MCP Connect. Lazy: a handful of people reach it, and only from an
/// AI app, so it stays out of everyone's first chunk (P1).
const oauthConsentRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.oauthConsent.path,
  validateSearch: z.object({ request: z.string().regex(/^[0-9A-Za-z]{32}$/) }).strict(),
  component: lazyRouteComponent(
    () => import("@/features/oauth-consent/routes/oauth-consent.route"),
    "OAuthConsentRoute",
  ),
});

/// PAYD-FR-042 (H-06). The alumni landing. Chrome free by design: a former
/// employee has no workspace, so there is nothing to wrap them in.
const alumniRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: ROUTES.alumni.path,
  validateSearch: z.object({}),
  component: lazyRouteComponent(
    () => import("@/features/alumni/routes/alumni-home.route"),
    "AlumniHomeRoute",
  ),
});

// -- Print layout (no app chrome at all) --------------------------------------
// The print-HTML archetype (attachments-and-exports.md section 9, first used by
// HRMS-PAY-023, joined by HRMS-REC-041's appointment letter). A third sibling of app-layout and auth-layout rather than a
// variant of either: these routes are workspace-scoped and signed-in, like
// app-layout, but the page IS the document, so a sidebar, a topbar or a toast
// would end up on the paper.
//
// `ActiveWorkspaceProvider` is the one piece of app-layout that stays: the
// routes resolve their workspace from the `$workspaceSlug` in their own path,
// exactly as every page inside the shell does.
function PrintLayoutShell() {
  const auth = useAuth();

  // The same two gates the app shell opens with, and for the same reason. Without
  // them `ActiveWorkspaceProvider` resolves the slug against an empty auth
  // payload, falls back to a placeholder workspace, and the print route fetches
  // a payslip from a workspace nobody is in: a real payslip would print as
  // "not available".
  if (auth.isLoading) {
    return <div className="min-h-svh bg-background" aria-hidden="true" />;
  }
  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <LoginRoute />
      </div>
    );
  }

  return (
    <ActiveWorkspaceProvider>
      <Outlet />
    </ActiveWorkspaceProvider>
  );
}

const printLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "print-layout",
  component: PrintLayoutShell,
});

const payslipPrintRoute = createRoute({
  getParentRoute: () => printLayoutRoute,
  path: ROUTES.workspace.payslipPrint.path,
  validateSearch: z.object({}).strict(),
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/payslip-print.route"),
    "PayslipPrintRoute",
  ),
});

const payrollRunPrintRoute = createRoute({
  getParentRoute: () => printLayoutRoute,
  path: ROUTES.workspace.payrollRunPrint.path,
  validateSearch: z.object({}).strict(),
  component: lazyRouteComponent(
    () => import("@/features/payroll/routes/payroll-run-print.route"),
    "PayrollRunPrintRoute",
  ),
});

const letterPrintRoute = createRoute({
  getParentRoute: () => printLayoutRoute,
  path: ROUTES.workspace.recruitmentLetterPrint.path,
  validateSearch: z.object({}).strict(),
  component: lazyRouteComponent(
    () => import("@/features/recruitment/routes/letter-print.route"),
    "LetterPrintRoute",
  ),
});

const routeTree = rootRoute.addChildren([
  appLayoutRoute.addChildren([
    indexRoute,
    designSystemRoute,
    settingsIndexRoute,
    settingsProfileRoute,
    settingsTokensRoute,
    settingsMcpRoute,
    settingsAiToolsRoute,
    settingsAiConnectionsRoute,
    settingsAccountRoute,
    settingsBillingRoute,
    accountSettingsRoute,
    accountBillingRoute,
    billingRedeemRoute,
    profileRoute,
    workspaceLayoutRoute.addChildren([
      workspaceHomeRoute,
      workspaceSettingsRoute,
      workspaceCompanySettingsRoute,
      peopleDirectoryRoute,
      peopleDetailRoute,
      peopleEditRoute,
      peopleDetailHrRoute,
      myTeamRoute,
      orgChartRoute,
      reviewsRoute,
      reviewsCyclesRoute,
      reviewsTemplatesRoute,
      reviewInsightsRoute,
      reviewsAppraisalSchedulesRoute,
      reviewCycleDetailRoute,
      reviewManagerRatingsRoute,
      reviewSelfReviewRoute,
      reviewManagerReviewRoute,
      reviewManagerResultsRoute,
      reviewPeerResponseRoute,
      reviewTemplateViewRoute,
      reviewTemplateEditorRoute,
      myTodosRoute,
      todoDetailRoute,
      adminTodosRoute,
      goalsRoute,
      goalDetailRoute,
      feedbackRoute,
      feedbackDetailRoute,
      adminFeedbackRoute,
      attendanceRoute,
      attendanceTeamRoute,
      attendanceRequestsRoute,
      attendanceBoardRoute,
      attendanceSetupRoute,
      leaveRoute,
      leaveHistoryRoute,
      leaveTeamRoute,
      leaveBalancesRoute,
      leaveSetupRoute,
      compensationRoute,
      compCycleRoute,
      payrollSetupRoute,
      payrollSalariesRoute,
      recruitmentRoute,
      recruitmentJobRoute,
      recruitmentRequisitionsRoute,
      recruitmentStaffingPlansRoute,
      recruitmentLettersRoute,
      recruitmentApplicantDetailRoute,
      recruitmentOffersRoute,
      recruitmentReferralsRoute,
      recruitmentInterviewsRoute,
      payrollInputsRoute,
      recoverySchedulesRoute,
      settlementsRoute,
      settlementWorksheetRoute,
      complianceDocumentsRoute,
      complianceDocumentRunRoute,
      payrollRunsRoute,
      payrollRunDetailRoute,
      myPayslipsRoute,
      myPayDetailsRoute,
      taxDeclarationsRoute,
      benefitsRoute,
      variablePayRoute,
      variablePayPlanRoute,
      variablePayPayoutRoute,
      parallelRunRoute,
      moneyAuditRoute,
      taxCalculatorRoute,
      hrSettingsRoute,
      approvalsRoute,
      expensesRoute,
      expenseAdvancesRoute,
      expenseTravelRoute,
      expenseClaimDetailRoute,
      kudosRoute,
      kudosDetailRoute,
      oneOnOneRoute,
      oneOnOneDetailRoute,
      oneOnOneWithRoute,
      notificationsRoute,
      notificationsPreferencesRoute,
      pulseSurveysNewRoute,
      pulseSurveysListRoute,
      pulseSurveyEditRoute,
      pulseSurveyInsightsRoute,
      pulseSurveyRespondRoute,
      myPulseRoute,
      myPulseAnsweredRoute,
      myPulseConversationRoute,
      pulseAnswerThreadsRoute,
      pulseAnswerThreadRoute,
      pulseSurveyDetailRoute,
      workspaceHealthConfigRoute,
      workspaceActionsRoute,
      myActionsRoute,
      actionUpdatesRoute,
      actionDetailRoute,
      workspaceRoute,
      dashboardDepartmentDetailRoute,
      dashboardThemeDetailRoute,
      dashboardFeedRoute,
      comparisonRoute,
      insightsRoute,
      legacyDashboardRootRedirect,
      legacyDashboardDeptRedirect,
      legacyDashboardThemeRedirect,
      legacyDashboardFeedRedirect,
    ]),
  ]),
  authLayoutRoute.addChildren([
    signupRoute,
    loginRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyRoute,
    authErrorRoute,
    acceptInviteRoute,
    googlePickAccountRoute,
    pricingRoute,
    publicApplyRoute,
    alumniRoute,
    oauthConsentRoute,
  ]),
  printLayoutRoute.addChildren([payslipPrintRoute, payrollRunPrintRoute, letterPrintRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
